import React, { useMemo, FC } from 'react'
import { Flex, Responsive, Switch, Text, TextBox } from '@revolut/ui-kit'
import styled from 'styled-components'

import { currencies } from 'constants/currencies'
import { RoundFlag } from 'components/RoundFlag'
import { formatMoney } from 'helpers/common'
import { Pocket } from 'types/common'

const joinToChain = (...chainParts: (string | null | undefined)[]) =>
  chainParts.filter(Boolean).join(' · ')

const RowContainer = styled(Flex).attrs({
  maxWidth: '24rem',
  justifyContent: 'space-between',
  alignItems: 'center',
})`
  &:last-child {
    margin-bottom: auto;
  }
`

const EllipsedFlexChildText = styled(Text)`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

type AccountDisplayProps = {
  account: Pocket
  name?: string
  onToggle?: (accountId: string, checked: boolean) => void
  selectedAccountIds?: string[]
  showFlag?: boolean
  icon?: React.ReactNode
  labelProps?: React.ComponentPropsWithoutRef<typeof Text> &
    React.ComponentPropsWithoutRef<typeof TextBox>
  balanceProps?: React.ComponentPropsWithoutRef<typeof Text> &
    React.ComponentPropsWithoutRef<typeof TextBox>
} & React.ComponentPropsWithoutRef<typeof Flex>

export const AccountDisplay: FC<AccountDisplayProps> = ({
  account,
  name,
  onToggle,
  selectedAccountIds,
  showFlag = true,
  icon,
  labelProps,
  balanceProps,
  ...rest
}) => {
  const { currency: currencyCode, balance, id: accountId } = account
  const formattedBalance = useMemo(
    () =>
      formatMoney({
        currency: currencyCode,
        amount: balance,
      }),
    [balance, currencyCode],
  )

  // Currency object by currenct string
  const currency = currencies[currencyCode]
  const currencyName = currency ? currency.currency : ''

  const accountNameString = name || joinToChain(currencyCode, currencyName)

  return (
    <Responsive breakpoint='md-*'>
      {({ isMatched }: { isMatched: boolean }) => (
        <RowContainer mb={{ all: '1rem', md: '1.5rem' }} {...rest}>
          <Flex flexDirection='row' alignItems='center' minWidth='0'>
            {showFlag && !icon && <RoundFlag size='xxl' currency={currencyCode} />}
            {icon}
            {isMatched ? (
              <EllipsedFlexChildText
                mr='s-16'
                ml={showFlag ? '1rem' : 0}
                title={accountNameString}
                {...labelProps}
              >
                {accountNameString}
              </EllipsedFlexChildText>
            ) : (
              <Flex flexDirection='column' ml='1rem' minWidth='0' flex='1'>
                <Text {...balanceProps}>{formattedBalance}</Text>
                <EllipsedFlexChildText mr='s-16' color='grey-50' {...labelProps}>
                  {accountNameString}
                </EllipsedFlexChildText>
              </Flex>
            )}
          </Flex>
          <Flex flexDirection='row'>
            {isMatched && (
              <TextBox color='grey-50' mr={onToggle ? '1rem' : 0} {...balanceProps}>
                {formattedBalance}
              </TextBox>
            )}
            {onToggle && (
              <Switch
                checked={selectedAccountIds && selectedAccountIds.includes(accountId)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { checked } = e.target
                  onToggle(accountId, checked)
                }}
              />
            )}
          </Flex>
        </RowContainer>
      )}
    </Responsive>
  )
}
